import React from "react";
import { motion } from "framer-motion";
import { Briefcase, Building2, Users, TrendingUp, Megaphone } from "lucide-react";

const FloatingParticle = ({ delay, x, y, size = 4 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${x}%`,
      top: `${y}%`,
      background: "linear-gradient(135deg, #60a5fa, #818cf8)",
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0],
      y: [0, -30, -60],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

const FellowsAdvisorySlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <FloatingParticle delay={0} x={15} y={25} size={5} />
      <FloatingParticle delay={1.0} x={80} y={55} size={4} />
      <FloatingParticle delay={0.5} x={50} y={75} size={6} />
      <FloatingParticle delay={1.8} x={70} y={20} size={3} />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1050px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl lg:text-[44px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Offer Your Own{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AI Dental Consulting Services.
            </span>
          </h2>
          <p className="text-[16px] text-slate-500 font-medium mt-4 max-w-2xl mx-auto">
            Once you've gone through the AI transformation with your own practice, you can package that experience and offer it to others.
          </p>
        </motion.div>

        {/* Two cards side by side */}
        <div className="w-full grid grid-cols-2 gap-6">
          {/* Card 1: How it works */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)" }} />

            <div className="p-7">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-5 shadow-lg shadow-blue-500/20 border border-blue-400">
                <Briefcase className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3">
                List Your Consulting Packages
              </h3>
              <p className="text-[14px] text-slate-500 leading-relaxed font-medium mb-5">
                Create your own consulting packages — workflow optimization, AI setup, staff training, practice transformation — whatever you're best at. List them on the Asha Marketplace and we'll promote them for you.
              </p>

              <div className="space-y-2.5">
                {[
                  { icon: Briefcase, label: "Design packages around your expertise", delay: 0.4 },
                  { icon: TrendingUp, label: "Set your own rates — you keep 85%, we keep 15%", delay: 0.5 },
                  { icon: Building2, label: "Available to practices & dental groups nationwide", delay: 0.6 },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.delay, duration: 0.4 }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-blue-50/80 border border-blue-100/60"
                  >
                    <item.icon className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-[13px] font-semibold text-slate-700">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Card 2: Built-in distribution */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)" }} />

            <div className="p-7">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-600 flex items-center justify-center mb-5 shadow-lg shadow-indigo-500/20 border border-indigo-400">
                <Megaphone className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3">
                We Bring You the Clients
              </h3>
              <p className="text-[14px] text-slate-500 leading-relaxed font-medium mb-5">
                Asha has a growing distribution to dental practices across the country that are actively interested in undergoing AI transformation. We'll promote your consulting services directly to them — helping you grow your influence and your business.
              </p>

              <div className="space-y-2.5">
                {[
                  { icon: Users, label: "Growing network of practices seeking AI guidance", delay: 0.5 },
                  { icon: Megaphone, label: "We actively promote your services to our network", delay: 0.6 },
                  { icon: TrendingUp, label: "Grow your reputation as an AI transformation leader", delay: 0.7 },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.delay, duration: 0.4 }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-indigo-50/80 border border-indigo-100/60"
                  >
                    <item.icon className="w-4 h-4 text-indigo-600 shrink-0" />
                    <span className="text-[13px] font-semibold text-slate-700">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FellowsAdvisorySlide;
